import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import SiteHeader from "@/components/SiteHeader";
import SiteFooter from "@/components/SiteFooter";
import OnboardingGate from "@/components/auth/OnboardingGate";
import QrCodeImage from "@/components/events/QrCodeImage";
import { useEvent } from "@/lib/events/useEvent";
import { encodeTicket } from "@/lib/events/ticket";
import { trackEvent } from "@/lib/analytics/track";
import { toast } from "@/hooks/use-toast";
import type { AppId } from "@/lib/apps";

const fmtWhen = (iso: string | null) => {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString(undefined, {
      weekday: "long",
      month: "long",
      day: "numeric",
      year: "numeric",
      hour: "numeric",
      minute: "2-digit",
    });
  } catch {
    return iso;
  }
};

const isAppId = (s: string): s is AppId =>
  s === "fryqnc" || s === "pulsynq" || s === "mindlynq";

const EventDetailInner = () => {
  const { eventId } = useParams<{ eventId: string }>();
  const {
    event,
    isHost,
    myRsvp,
    myGuests,
    allRsvps,
    allGuests,
    loading,
    rsvp,
    cancelRsvp,
    probeCapacity,
    capacityStats,
  } = useEvent(eventId);

  const [plusOnes, setPlusOnes] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [seatsLeft, setSeatsLeft] = useState<number | null>(null);
  const [capacityKnown, setCapacityKnown] = useState<number | null>(null);

  // Surface a public-facing capacity badge so non-hosts know if "going" will
  // be auto-waitlisted before they tap.
  useEffect(() => {
    if (!event) return;
    if (isHost && capacityStats) {
      setCapacityKnown(capacityStats.capacity);
      setSeatsLeft(capacityStats.seats_left);
      return;
    }
    let cancelled = false;
    void probeCapacity().then((p) => {
      if (cancelled) return;
      setCapacityKnown(p.capacity);
      setSeatsLeft(p.seats_left);
    });
    return () => {
      cancelled = true;
    };
  }, [event, isHost, capacityStats, probeCapacity]);

  if (loading) {
    return (
      <main className="mx-auto max-w-3xl px-6 py-12 text-sm text-muted-foreground">
        Loading…
      </main>
    );
  }
  if (!event) {
    return (
      <main className="mx-auto max-w-3xl px-6 py-12">
        <h1 className="text-2xl font-semibold">Event not found</h1>
        <Link
          to="/events"
          className="mt-4 inline-flex text-sm text-muted-foreground hover:text-foreground"
        >
          ← Back to events
        </Link>
      </main>
    );
  }

  const goingCount = allRsvps.filter((r) => r.status === "going").length;
  const checkedInCount = allGuests.filter((g) => g.checked_in).length;
  const appProp = isAppId(event.app) ? event.app : null;
  const requestedTickets =
    1 +
    plusOnes
      .split(",")
      .map((s) => s.trim())
      .filter(Boolean).length;
  const willWaitlist =
    capacityKnown != null && seatsLeft != null && seatsLeft < requestedTickets;

  const handleGoing = async () => {
    setErr(null);
    setBusy(true);
    const names = plusOnes
      .split(",")
      .map((s) => s.trim())
      .filter(Boolean);
    const res = await rsvp("going", names);
    setBusy(false);
    if ("error" in res) {
      setErr(res.error);
      return;
    }
    setPlusOnes("");
    trackEvent("event_rsvp", {
      app: appProp,
      props: {
        event_id: event.id,
        status: res.status,
        guests: names.length,
        auto_waitlisted: res.status === "waitlist",
      },
    });
    toast({
      title: res.status === "going" ? "You're going!" : "Added to waitlist",
      description:
        res.status === "going"
          ? "Show your QR at the door."
          : "We'll bump you up if a seat opens.",
    });
  };

  const handleWaitlist = async () => {
    setErr(null);
    setBusy(true);
    const res = await rsvp("waitlist");
    setBusy(false);
    if ("error" in res) {
      setErr(res.error);
      return;
    }
    trackEvent("event_rsvp", {
      app: appProp,
      props: { event_id: event.id, status: "waitlist", guests: 0 },
    });
    toast({
      title: "Joined waitlist",
      description: "We'll bump you up if a seat opens.",
    });
  };

  const handleCancel = async () => {
    setBusy(true);
    const res = await cancelRsvp();
    setBusy(false);
    if ("error" in res) {
      setErr(res.error);
      return;
    }
    trackEvent("event_rsvp", {
      app: appProp,
      props: {
        event_id: event.id,
        status: "canceled",
        promoted_user_id: res.promoted_user_id ?? null,
      },
    });
    toast({
      title: "RSVP cancelled",
      description: res.promoted_user_id
        ? "Someone from the waitlist was bumped up."
        : "Your seat is released.",
    });
  };

  return (
    <main className="mx-auto max-w-3xl px-6 py-10">
      {event.cover_image_url ? (
        <img
          src={event.cover_image_url}
          alt=""
          className="mb-6 aspect-[16/9] w-full rounded-2xl object-cover"
        />
      ) : null}

      <header className="mb-6">
        <p className="text-xs font-medium uppercase tracking-[0.2em] text-muted-foreground">
          {event.app}
          {event.status !== "published" ? ` · ${event.status}` : ""}
        </p>
        <h1 className="mt-2 text-3xl md:text-4xl font-semibold tracking-tight">
          {event.title}
        </h1>
        <p className="mt-2 text-sm text-muted-foreground">{fmtWhen(event.starts_at)}</p>
        {event.location_text ? (
          <p className="text-sm text-muted-foreground">{event.location_text}</p>
        ) : null}
        {capacityKnown != null ? (
          <p className="mt-2 inline-flex rounded-full bg-muted px-3 py-1 text-xs font-medium text-muted-foreground">
            {seatsLeft === 0
              ? `Sold out — ${capacityKnown} cap`
              : `${seatsLeft ?? "?"} of ${capacityKnown} seats left`}
          </p>
        ) : null}
      </header>

      {event.description ? (
        <p className="mb-8 whitespace-pre-line text-sm leading-relaxed">
          {event.description}
        </p>
      ) : null}

      {/* RSVP block */}
      <section className="mb-8 rounded-2xl border border-border p-5">
        <h2 className="text-sm font-semibold uppercase tracking-wide text-muted-foreground">
          Your RSVP
        </h2>
        {myRsvp ? (
          <div className="mt-3 space-y-4">
            <p className="text-sm">
              You're <span className="font-medium">{myRsvp.status}</span>
              {myGuests.length > 1
                ? ` with ${myGuests.length - 1} guest${myGuests.length - 1 > 1 ? "s" : ""}`
                : ""}
              .
            </p>

            {myRsvp.status === "going" && myGuests.length > 0 ? (
              <div className="space-y-3">
                <p className="text-xs text-muted-foreground">
                  Show this at the door — host scans to check you in.
                </p>
                <div className="grid gap-4 sm:grid-cols-2">
                  {myGuests.map((g) => (
                    <div
                      key={g.id}
                      className="flex flex-col items-center gap-2 rounded-xl border border-border p-4"
                    >
                      <QrCodeImage value={encodeTicket(event.id, g.id)} size={180} />
                      <p className="text-xs font-medium">
                        {g.guest_name ?? "You"}
                      </p>
                      {g.checked_in ? (
                        <span className="rounded-full bg-emerald-500/15 px-2 py-0.5 text-xs font-medium text-emerald-700 dark:text-emerald-300">
                          Checked in
                        </span>
                      ) : (
                        <span className="rounded-full bg-muted px-2 py-0.5 text-xs text-muted-foreground">
                          Not checked in
                        </span>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            ) : null}

            {myRsvp.status === "waitlist" ? (
              <p className="rounded-xl bg-muted/50 px-3 py-2 text-xs text-muted-foreground">
                You're on the waitlist. We'll move you to going automatically
                when a seat opens.
              </p>
            ) : null}

            <div className="flex flex-wrap gap-2">
              {myRsvp.status !== "going" ? (
                <button
                  type="button"
                  onClick={handleGoing}
                  disabled={busy}
                  className="rounded-full bg-foreground text-background px-4 py-2 text-xs font-medium disabled:opacity-50"
                >
                  {willWaitlist ? "Try going (waitlist if full)" : "I'm going"}
                </button>
              ) : null}
              <button
                type="button"
                onClick={handleCancel}
                disabled={busy}
                className="rounded-full border border-border px-4 py-2 text-xs font-medium hover:bg-muted disabled:opacity-50"
              >
                Cancel RSVP
              </button>
            </div>
            {err ? <p className="text-xs text-destructive">{err}</p> : null}
          </div>
        ) : (
          <div className="mt-3 space-y-3">
            <label className="block text-xs text-muted-foreground">
              Bringing guests? Names, comma separated (optional)
            </label>
            <input
              value={plusOnes}
              onChange={(e) => setPlusOnes(e.target.value)}
              placeholder="Alex, Sam"
              className="w-full rounded-lg border border-border bg-background p-2 text-sm"
            />
            {willWaitlist ? (
              <p className="text-xs text-amber-700 dark:text-amber-300">
                Only {seatsLeft} seat{seatsLeft === 1 ? "" : "s"} left — you'll
                join the waitlist if there's no room for {requestedTickets}.
              </p>
            ) : null}
            <div className="flex flex-wrap gap-2">
              <button
                type="button"
                onClick={handleGoing}
                disabled={busy}
                className="rounded-full bg-foreground text-background px-4 py-2 text-xs font-medium disabled:opacity-50"
              >
                {busy ? "Saving…" : willWaitlist ? "Try going (waitlist if full)" : "I'm going"}
              </button>
              <button
                type="button"
                onClick={handleWaitlist}
                disabled={busy}
                className="rounded-full border border-border px-4 py-2 text-xs font-medium hover:bg-muted disabled:opacity-50"
              >
                Join waitlist
              </button>
            </div>
            {err ? <p className="text-xs text-destructive">{err}</p> : null}
          </div>
        )}
      </section>

      {/* Host tools */}
      {isHost ? (
        <section className="rounded-2xl border border-border p-5">
          <div className="flex flex-wrap items-end justify-between gap-3">
            <div>
              <h2 className="text-sm font-semibold uppercase tracking-wide text-muted-foreground">
                Host tools
              </h2>
              <p className="mt-1 text-sm">
                {goingCount} going · {allGuests.length} ticket
                {allGuests.length === 1 ? "" : "s"} · {checkedInCount} checked in
                {capacityStats?.waitlist_count
                  ? ` · ${capacityStats.waitlist_count} waitlisted`
                  : ""}
              </p>
            </div>
            <Link
              to={`/events/${event.id}/check-in`}
              className="rounded-full bg-foreground text-background px-4 py-2 text-xs font-medium"
            >
              Open check-in scanner
            </Link>
          </div>

          {allGuests.length > 0 ? (
            <ul className="mt-4 divide-y divide-border rounded-lg border border-border">
              {allGuests.map((g) => (
                <li
                  key={g.id}
                  className="flex items-center justify-between gap-3 p-3 text-sm"
                >
                  <span>{g.guest_name ?? "Primary attendee"}</span>
                  {g.checked_in ? (
                    <span className="rounded-full bg-emerald-500/15 px-2 py-0.5 text-xs font-medium text-emerald-700 dark:text-emerald-300">
                      Checked in
                    </span>
                  ) : (
                    <span className="text-xs text-muted-foreground">Not yet</span>
                  )}
                </li>
              ))}
            </ul>
          ) : null}
        </section>
      ) : null}

      <Link
        to="/events"
        className="mt-8 inline-flex text-xs text-muted-foreground hover:text-foreground"
      >
        ← Back to events
      </Link>
    </main>
  );
};

const EventDetail = () => (
  <div className="min-h-screen flex flex-col bg-background text-foreground">
    <SiteHeader />
    <OnboardingGate>
      <EventDetailInner />
    </OnboardingGate>
    <SiteFooter />
  </div>
);

export default EventDetail;
